(() => {
  if (customElements.get('yahav-carousel')) return;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class YahavCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;
      this.cards = [...this.track.querySelectorAll('[data-carousel-card]')];
      this.dots = [...this.querySelectorAll('[data-carousel-dot]')];
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('click', (event) => {
        const previous = event.target.closest('[data-carousel-prev]');
        const next = event.target.closest('[data-carousel-next]');
        const dot = event.target.closest('[data-carousel-dot]');
        const play = event.target.closest('[data-video-play]');
        if (previous) this.goTo(this.current - 1);
        if (next) this.goTo(this.current + 1);
        if (dot) this.goTo(Number(dot.dataset.carouselDot));
        if (play) this.playVideo(play.closest('[data-carousel-card]'));
      }, options);
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.current + (event.key === 'ArrowRight' ? 1 : -1));
        }
        if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.goTo(event.key === 'Home' ? 0 : this.positions.length - 1);
        }
      }, options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      document.addEventListener('shopify:block:select', (event) => {
        const card = event.target.closest('[data-carousel-card]');
        if (!card || !this.contains(card)) return;
        this.pauseVideos();
        this.track.scrollTo({ left: this.cardPosition(card), behavior: 'auto' });
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseVideos();
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.track);
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (!entry.isIntersecting) this.stopVideo(entry.target); });
      }, { root: this.track, threshold: 0.1 });
      this.cards.forEach((card) => this.visibilityObserver.observe(card));
      this.sectionVisibilityObserver = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) this.pauseVideos();
      });
      this.sectionVisibilityObserver.observe(this);
      this.measure();
      if (this.dataset.initialSlide) {
        const initial = Math.min(Number(this.dataset.initialSlide), this.cards.length - 1);
        if (this.cards[initial]) this.track.scrollLeft = this.cardPosition(this.cards[initial]);
      }
      this.update();
    }

    cardPosition(card) {
      const trackRect = this.track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const raw = cardRect.left - trackRect.left + this.track.scrollLeft;
      const offset = this.dataset.centered === 'true' ? (this.track.clientWidth - card.clientWidth) / 2 : 0;
      return Math.max(0, Math.min(raw - offset, this.track.scrollWidth - this.track.clientWidth));
    }

    measure() {
      const oldIndex = this.current || 0;
      let cards = this.cards;
      // Paged carousels step a whole view at a time; the last page is clamped so it is full too.
      if (this.dataset.paged === 'true' && cards.length) {
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const perView = Math.max(1, Math.round((this.track.clientWidth + gap) / (cards[0].offsetWidth + gap)));
        cards = cards.filter((card, index) => index % perView === 0);
      }
      this.positions = [...new Set(cards.map((card) => Math.round(this.cardPosition(card))))];
      this.dataset.overflow = String(this.positions.length > 1);
      this.dots.forEach((dot, index) => {
        dot.hidden = index >= this.positions.length;
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      });
      this.current = Math.min(oldIndex, Math.max(0, this.positions.length - 1));
      this.update();
    }

    update() {
      if (!this.positions?.length) return;
      this.current = this.positions.reduce((best, position, index) =>
        Math.abs(position - this.track.scrollLeft) < Math.abs(this.positions[best] - this.track.scrollLeft) ? index : best, 0);
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.current)));
      const previous = this.querySelector('[data-carousel-prev]');
      const next = this.querySelector('[data-carousel-next]');
      if (previous) previous.disabled = this.current === 0;
      if (next) next.disabled = this.current >= this.positions.length - 1;
    }

    goTo(index) {
      const next = Math.max(0, Math.min(index, this.positions.length - 1));
      this.pauseVideos();
      this.track.scrollTo({ left: this.positions[next], behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    async playVideo(card) {
      if (!card) return;
      const template = card.querySelector('[data-video-template]');
      if (!template) return;
      this.pauseVideos(card);
      const player = card.querySelector('[data-video-player]');
      const button = card.querySelector('[data-video-play]');
      const poster = card.querySelector('[data-video-poster]');
      card.querySelector('[data-video-error]')?.remove();
      player.replaceChildren(template.content.cloneNode(true));
      button.hidden = true;
      if (poster) poster.hidden = true;
      card.classList.add('is-playing');
      const video = player.querySelector('video');
      if (video) {
        video.controls = true;
        video.playsInline = true;
        try { await video.play(); } catch (error) {
          this.stopVideo(card);
          const notice = document.createElement('p');
          notice.dataset.videoError = '';
          notice.className = 'yahav-how__notice';
          notice.setAttribute('role', 'status');
          notice.textContent = 'The video could not play. Please try again.';
          card.append(notice);
        }
      }
    }

    stopVideo(card) {
      const player = card.querySelector('[data-video-player]');
      if (!player || !player.childElementCount) return;
      player.querySelector('video')?.pause();
      player.replaceChildren();
      card.classList.remove('is-playing');
      const button = card.querySelector('[data-video-play]');
      const poster = card.querySelector('[data-video-poster]');
      if (button) button.hidden = false;
      if (poster) poster.hidden = false;
    }

    pauseVideos(except) { this.cards.forEach((card) => { if (card !== except) this.stopVideo(card); }); }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.sectionVisibilityObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      if (this.cards) this.pauseVideos();
    }
  }

  customElements.define('yahav-carousel', YahavCarousel);

  class YahavFaq extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('toggle', (event) => {
        if (this.dataset.singleOpen !== 'true' || !event.target.open) return;
        this.querySelectorAll('details[open]').forEach((item) => { if (item !== event.target) item.open = false; });
      }, { ...options, capture: true });
      document.addEventListener('shopify:block:select', (event) => {
        const item = event.target.closest('details');
        if (item && this.contains(item)) item.open = true;
      }, options);
    }
    disconnectedCallback() { this.controller?.abort(); }
  }
  customElements.define('yahav-faq', YahavFaq);

  // Card size pickers: show the chosen size's price.
  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-yahav-variant-select]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const card = select.closest('article, .wws-video-carousel__card') || select.form;
    if (!option || !card) return;
    const price = card.querySelector('[data-yahav-variant-price]');
    const compare = card.querySelector('[data-yahav-variant-compare]');
    const saving = card.querySelector('[data-yahav-variant-saving]');
    if (price) price.textContent = option.dataset.price;
    if (compare) { compare.textContent = option.dataset.compare; compare.hidden = !option.dataset.compare; }
    if (saving) saving.hidden = true;
  });

  // Scroll reveal for [data-yahav-reveal]: plays once, only when theme animations are on.
  // Items already on screen at load are left as they are, so nothing flashes.
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.dataset.yahavReveal = 'in';
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }) : null;

  const initReveal = (root = document) => {
    if (!revealObserver || !document.body.classList.contains('aos-initialized')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    root.querySelectorAll('[data-yahav-reveal]:not([data-yahav-reveal="in"])').forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) return;
      item.dataset.yahavReveal = 'pending';
      revealObserver.observe(item);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initReveal());
  else initReveal();
  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
})();
